import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, MapPin, Lock, ArrowLeft } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { useStore } from '../../context/StoreContext';
import { getCategoryIcon } from '../../utils/categoryIcons';
import { CartLineUpsellTeaser } from '../Cart/CartLineUpsellTeaser';

// Checkout Page - Mock payment flow (actual pay doesn't need to work)
export const CheckoutPage = () => {
  const { cartItems, getCartTotal, completePurchase } = useCart();
  const { selectedStoreData } = useStore();
  const navigate = useNavigate();
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
  });
  const [cardNumber, setCardNumber] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      const purchaseData = completePurchase();
      navigate('/thank-you', { state: { orderData: purchaseData } });
    }, 800);
  };

  const total = getCartTotal();

  return (
    <main className="checkout-page" style={{ padding: '24px 16px', maxWidth: 900, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/cart')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 'none',
          color: '#4B164C',
          fontSize: 16,
          cursor: 'pointer',
          marginBottom: 24,
          fontWeight: 500,
        }}
      >
        <ArrowLeft size={20} />
        Retour au panier
      </button>

      <h1 style={{ fontSize: 28, color: '#4B164C', marginBottom: 8, fontWeight: 700 }}>Finaliser la commande</h1>
      <p style={{ color: '#888', marginBottom: 32 }}>Remplissez les informations ci-dessous (simulation)</p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {/* Shipping Address */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 18, color: '#4B164C', marginBottom: 20 }}>
              <MapPin size={22} weight="bold" />
              Adresse de livraison
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <input
                type="text"
                placeholder="Nom complet"
                value={shippingAddress.name}
                onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                style={{ padding: 12, borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 15 }}
              />
              <input
                type="text"
                placeholder="Adresse"
                value={shippingAddress.address}
                onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                style={{ padding: 12, borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 15, gridColumn: '1 / -1' }}
              />
              <input
                type="text"
                placeholder="Ville"
                value={shippingAddress.city}
                onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                style={{ padding: 12, borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 15 }}
              />
              <input
                type="text"
                placeholder="Code postal"
                value={shippingAddress.postalCode}
                onChange={(e) => setShippingAddress({ ...shippingAddress, postalCode: e.target.value })}
                style={{ padding: 12, borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 15 }}
              />
              <input
                type="tel"
                placeholder="Téléphone"
                value={shippingAddress.phone}
                onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                style={{ padding: 12, borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 15, gridColumn: '1 / -1' }}
              />
            </div>
          </div>

          {/* Payment */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 18, color: '#4B164C', marginBottom: 20 }}>
              <CreditCard size={22} weight="bold" />
              Paiement (simulation)
            </h2>
            <input
              type="text"
              placeholder="Numéro de carte: 4242 4242 4242 4242"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              style={{ padding: 12, borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 15, width: '100%' }}
            />
            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              <Lock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Le paiement n'est pas réel. Cliquez sur « Payer » pour simuler la commande.
            </p>
          </div>

          {/* Order Summary */}
          <div style={{ background: '#f8f6fa', borderRadius: 16, padding: 24 }}>
            <h2 style={{ fontSize: 18, color: '#4B164C', marginBottom: 20, fontWeight: 600 }}>Récapitulatif</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {cartItems.map((item) => {
                const storeData = item.store_data?.[`store_${(item.selectedStore || '1').toString().padStart(3, '0')}`];
                const price = storeData?.price || item.price || 0;
                const imageUrl = item.image ? (item.image.startsWith('http') ? item.image : `https:${item.image}`) : null;
                const IconComponent = getCategoryIcon(item.categorias_flat);
                return (
                  <div key={item.objectID}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, background: '#fff', borderRadius: 12 }}>
                      <div style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: '#f0e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <IconComponent size={24} color="#4B164C" />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#4B164C', fontSize: 14 }}>{item.name}</div>
                        <div style={{ fontSize: 13, color: '#888' }}>Qté: {item.quantity} × ${price.toLocaleString('fr-CA', { minimumFractionDigits: 2 })}</div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#4B164C' }}>${(price * item.quantity).toLocaleString('fr-CA', { minimumFractionDigits: 2 })}</div>
                    </div>
                    <CartLineUpsellTeaser objectID={item.objectID} />
                  </div>
                );
              })}
            </div>
            <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: '#4B164C' }}>
                <span>Total</span>
                <span>${total.toLocaleString('fr-CA', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isProcessing}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              width: '100%',
              padding: 16,
              background: isProcessing ? '#999' : '#4B164C',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 18,
              fontWeight: 600,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            <CreditCard size={24} />
            {isProcessing ? 'Traitement...' : 'Payer'}
          </button>
        </div>
      </form>
    </main>
  );
};
